import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Helper function to get a cookie value by name
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

// Helper function to set a cookie
const setCookie = (name: string, value: string, days: number): void => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
};

/**
 * LocalStorageNotice component displays a notification about localStorage usage
 * and allows users to consent to its use.
 */
function LocalStorageNotice() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    // Check both localStorage and cookies for consent
    const localStorageConsent = (() => {
      try {
        return localStorage.getItem('localStorage_consent') === 'true';
      } catch (e) {
        return false;
      }
    })();
    
    const cookieConsent = getCookie('localStorage_consent') === 'true';
    
    // Only show if neither localStorage nor cookie consent exists
    if (!localStorageConsent && !cookieConsent) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    // Start the fade-out animation
    setIsFading(true);
    
    // Try both storage methods to maximize compatibility
    try {
      localStorage.setItem('localStorage_consent', 'true');
    } catch (e) {
      // Silent failure - cookie will be the fallback
    }
    
    // Always set the cookie (will work in most environments)
    setCookie('localStorage_consent', 'true', 365); // 1 year
    
    // Wait for the animation to complete
    setTimeout(() => {
      setIsVisible(false);
      
      // Manually trigger any components that depend on the consent state
      if (window.dispatchEvent) {
        const consentEvent = new Event('localStorageConsentChanged');
        window.dispatchEvent(consentEvent);
      }
    }, 300); // Match this with the transition duration
  };

  const handleDismiss = () => {
    // Start the fade-out animation
    setIsFading(true);
    
    // Wait for the animation to complete
    setTimeout(() => {
      // Only hide the notice temporarily without setting consent
      setIsVisible(false);
    }, 300); // Match this with the transition duration
  };

  if (!isVisible) {
    return null; // Don't render anything if the notice isn't visible
  }

  return (
    <div className="toast-container position-fixed bottom-0 start-50 translate-middle-x p-3" style={{ zIndex: 1050 }}>
      <div 
        className={`toast show ${isFading ? 'opacity-0' : 'opacity-100'}`} 
        role="alert" 
        aria-live="assertive" 
        aria-atomic="true"
        style={{ transition: 'opacity 300ms ease-out' }}
      >
        <div className="toast-header">
          <strong className="me-auto">{t('localStorageNotice.title', 'Cookie Consent')}</strong>
          <button 
            type="button" 
            className="btn-close" 
            aria-label="Close"
            onClick={handleDismiss}
          ></button>
        </div>
        <div className="toast-body">
          <p className="mb-3 small">
            {t('localStorageNotice.text', 'We use browser local storage to remember your preferences like theme, language, and user type. No personal data is sent to our servers.')}
            {' '}
            <Link to="/privacy-policy" className="link-primary">{t('localStorageNotice.privacyLink', 'Privacy Policy')}</Link>
          </p>
          <div className="d-flex justify-content-end">
            <button 
              className="btn btn-sm btn-primary"
              onClick={handleAccept}
              aria-label={t('localStorageNotice.accept', 'Accept')}
            >
              {t('localStorageNotice.accept', 'Accept')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LocalStorageNotice; 